import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Outlet, useOutletContext } from "react-router-dom";

import { SpinnerLoader } from "components/loader/loader";
import { type KeystoneOfferEligibility } from "data/keystone-api-mapping";
import { useClaimsConstants, usePoliciesList } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { useHideSupportChat, useFlags } from "utils";

import { useMultiPolicyEligibility } from "../hooks";
import styles from "./shingle-reseal.module.scss";

export type WindDefenseContextState = {
  /** Keystone policy IDs chosen on the property step for Wind Defense enrollment. */
  selectedWindDefensePolicyIds?: string[];
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  phone?: string;
  eligibleWindDefensePolicies?: Record<string, unknown>[];
  termsSageSure?: string;
  termsLiftLock?: string;
  termsSageSureAgreed?: boolean;
  termsLiftLockAgreed?: boolean;
  offeringId?: string;
  offerId?: string;
} | null;

export const WindDefenseContext = createContext<{
  state: WindDefenseContextState;
  setState: Dispatch<SetStateAction<WindDefenseContextState>>;
}>({ state: null, setState: () => null });

export const WindDefenseChecker = () => {
  useHideSupportChat();
  const { setFields, state } =
    useMultiStepFormState<WindDefenseContextState>(WindDefenseContext);
  const { windDefenseReduceFlow } = useFlags();
  const { data: claimsConstants, isLoading } = useClaimsConstants();
  const policyHolderPhone = claimsConstants?.PolicyHolderDetails.phoneNumber;
  const { data: policies } = usePoliciesList();
  const eligibilityData = useOutletContext<KeystoneOfferEligibility>();
  const skipMultiPolicyEligibilityCheck =
    !windDefenseReduceFlow || policies?.length === 1;

  const { additionalPolicyEligibilityData, loading: eligibilityLoading } =
    useMultiPolicyEligibility(
      eligibilityData?.insightPolicyId,
      "wind-defense",
      skipMultiPolicyEligibilityCheck,
    );

  const allEligibility = useMemo<KeystoneOfferEligibility[]>(
    () =>
      eligibilityData
        ? [eligibilityData, ...additionalPolicyEligibilityData]
        : [],
    [eligibilityData, additionalPolicyEligibilityData],
  );

  useEffect(() => {
    if (!policies || allEligibility.length === 0) return;
    const windDefenseEligiblePolicies = policies
      .filter((p) =>
        allEligibility.some((o) => o.insightPolicyId === p.keystonePolicyId),
      )
      .map((p) => ({
        ...p,
        eligibilityDetails: allEligibility.find(
          (o) => o.insightPolicyId === p.keystonePolicyId,
        ),
      }));
    setFields({ eligibleWindDefensePolicies: windDefenseEligiblePolicies });
  }, [policies, setFields, allEligibility]);

  useEffect(() => {
    const termsSageSure = eligibilityData?.additionalDetails?.find(
      (detail) => detail.assetTypeCode === "INTERNAL_TERMS_OF_USE",
    );
    const termsLiftLock = eligibilityData?.additionalDetails?.find(
      (detail) => detail.assetTypeCode === "PROVIDER_TERMS_OF_USE",
    );
    setFields({
      termsSageSure: termsSageSure?.assetTextContent,
      termsLiftLock: termsLiftLock?.assetTextContent,
    });
  }, [eligibilityData?.additionalDetails, setFields]);

  useEffect(() => {
    if (eligibilityData?.offeringId) {
      setFields({ offeringId: eligibilityData.offeringId });
    }
  }, [eligibilityData?.offeringId, setFields]);

  useEffect(() => {
    if (eligibilityData?.offerId) {
      setFields({ offerId: eligibilityData.offerId });
    }
  }, [eligibilityData?.offerId, setFields]);

  useEffect(() => {
    if (policyHolderPhone && !state?.phone) {
      setFields({ phone: policyHolderPhone ?? "" });
    }
  }, [policyHolderPhone, setFields, state?.phone]);

  if (windDefenseReduceFlow) {
    if (eligibilityLoading) {
      return (
        <div className={styles.loaderWrapper}>
          <SpinnerLoader />
        </div>
      );
    }
  } else {
    if (isLoading) {
      return (
        <div className={styles.loaderWrapper}>
          <SpinnerLoader />
        </div>
      );
    }
  }

  return <Outlet />;
};

export const WindDefenseFlow = () => {
  const [state, setState] = useState<WindDefenseContextState>(null);
  return (
    <WindDefenseContext.Provider value={{ state, setState }}>
      <WindDefenseChecker />
    </WindDefenseContext.Provider>
  );
};
